import { A, useSearchParams } from '@solidjs/router';
import {
  type Component,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  onCleanup,
  Show,
} from 'solid-js';
import {
  cancelInteraction,
  fetchAgents,
  fetchInteraction,
  fetchInteractions,
  fetchInteractionTypes,
  fetchProjects,
  respondToInteraction,
  type Interaction,
  type InteractionType,
} from '../services/api';

type ViewMode = 'list' | 'detail';
type PageSize = 30 | 50 | 100;

const Interactions: Component = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Pagination state
  const [pageSize, setPageSize] = createSignal<PageSize>(30);
  const [currentPage, setCurrentPage] = createSignal(1);

  // Filter state
  const [selectedStatus, setSelectedStatus] = createSignal<string>(searchParams.status || '');
  const [selectedPriority, setSelectedPriority] = createSignal<string>(searchParams.priority || '');
  const [selectedProjectId, setSelectedProjectId] = createSignal<string>(
    searchParams.projectId || '',
  );
  const [selectedType, setSelectedType] = createSignal<string>(searchParams.type || '');

  // UI state
  const [viewMode, setViewMode] = createSignal<ViewMode>('list');
  const [selectedInteractionId, setSelectedInteractionId] = createSignal<string | null>(
    searchParams.interactionId || null,
  );
  const [showCancelDialog, setShowCancelDialog] = createSignal(false);
  const [isCancelling, setIsCancelling] = createSignal(false);
  const [autoRefresh, setAutoRefresh] = createSignal(true);

  // Response state for detail view
  const [isResponding, setIsResponding] = createSignal(false);

  // Computed pagination params
  const paginationParams = createMemo(() => ({
    limit: pageSize(),
    offset: (currentPage() - 1) * pageSize(),
    status: selectedStatus() || undefined,
    priority: selectedPriority() || undefined,
    projectId: selectedProjectId() || undefined,
    type: selectedType() || undefined,
  }));

  // Fetch interactions with pagination
  const [interactionsData, { refetch }] = createResource(paginationParams, fetchInteractions);

  // Fetch projects for filter dropdown
  const [projectsData] = createResource(async () => {
    return fetchProjects({ limit: 100 });
  });

  // Fetch agents for display
  const [agentsData] = createResource(async () => {
    return fetchAgents({ limit: 100 });
  });

  // Fetch interaction types for filter dropdown
  const [interactionTypesData] = createResource(fetchInteractionTypes);

  // Fetch selected interaction details
  const [selectedInteraction, { refetch: refetchSelectedInteraction }] = createResource(
    () => selectedInteractionId(),
    async (id) => {
      if (!id) return null;
      return fetchInteraction(id);
    },
  );

  // Auto-refresh for pending interactions
  createEffect(() => {
    if (!autoRefresh()) return;

    const interval = setInterval(() => {
      refetch();
      if (selectedInteractionId() && selectedInteraction()?.status === 'pending') {
        refetchSelectedInteraction();
      }
    }, 5000);

    onCleanup(() => clearInterval(interval));
  });

  // Derived values
  const interactions = createMemo(() => interactionsData()?.items || []);
  const totalCount = createMemo(() => interactionsData()?.total || 0);
  const totalPages = createMemo(() => Math.ceil(totalCount() / pageSize()));
  const projects = createMemo(() => projectsData()?.items || []);
  const agents = createMemo(() => agentsData()?.items || []);
  const interactionTypes = createMemo(() => interactionTypesData() || []);

  // Reset to page 1 when filters change
  createEffect(() => {
    selectedStatus();
    selectedPriority();
    selectedProjectId();
    selectedType();
    pageSize();
    setCurrentPage(1);
  });

  // Count pending interactions
  const pendingCount = createMemo(() => {
    return interactions().filter((i) => i.status === 'pending').length;
  });

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (seconds < 60) return `${seconds}s ago`;
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'in_progress':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'completed':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'cancelled':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      case 'timed_out':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'normal':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'low':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getInteractionTypeIcon = (typeId: string) => {
    const type = interactionTypes().find((t) => t.id === typeId);
    if (!type) return 'question';

    switch (type.slug) {
      case 'approval':
        return 'check-circle';
      case 'confirmation':
        return 'shield-check';
      case 'multiple-choice':
        return 'list';
      case 'free-text':
        return 'edit';
      case 'code-review':
        return 'code';
      case 'discussion':
        return 'chat';
      case 'file-selection':
        return 'folder';
      case 'priority-ranking':
        return 'sort';
      case 'poll':
        return 'chart-bar';
      case 'brainstorm':
        return 'lightbulb';
      case 'inspiration':
        return 'sparkles';
      default:
        return 'question';
    }
  };

  const clearFilters = () => {
    setSelectedStatus('');
    setSelectedPriority('');
    setSelectedProjectId('');
    setSelectedType('');
    setCurrentPage(1);
    setSearchParams({});
  };

  const hasActiveFilters = createMemo(
    () =>
      selectedStatus() !== '' ||
      selectedPriority() !== '' ||
      selectedProjectId() !== '' ||
      selectedType() !== '',
  );

  const handleSelectInteraction = (interactionId: string) => {
    setSelectedInteractionId(interactionId);
    setViewMode('detail');
    setSearchParams({ interactionId });
  };

  const handleBackToList = () => {
    setSelectedInteractionId(null);
    setViewMode('list');
    setSearchParams({ interactionId: undefined });
  };

  const handleCancelInteraction = async () => {
    if (!selectedInteractionId()) return;

    setIsCancelling(true);
    try {
      await cancelInteraction(selectedInteractionId()!);
      refetchSelectedInteraction();
      refetch();
    } catch (error) {
      console.error('Failed to cancel interaction:', error);
      alert('Failed to cancel interaction. Please try again.');
    } finally {
      setIsCancelling(false);
      setShowCancelDialog(false);
    }
  };

  const getProjectName = (projectId?: string) => {
    if (!projectId) return '-';
    const project = projects().find((p) => p.id === projectId);
    return project?.name || 'Unknown Project';
  };

  const getAgentName = (agentId?: string) => {
    if (!agentId) return '-';
    const agent = agents().find((a) => a.id === agentId);
    return agent?.name || 'Unknown Agent';
  };

  const getInteractionTypeName = (typeId: string) => {
    const type = interactionTypes().find((t) => t.id === typeId);
    return type?.name || typeId;
  };

  // Render the appropriate interaction response UI based on type
  const renderInteractionResponseUI = (interaction: Interaction) => {
    const type = interactionTypes().find((t) => t.id === interaction.interactionTypeId);
    if (!type) return null;

    const payload = interaction.requestPayload as Record<string, unknown>;

    switch (type.slug) {
      case 'approval':
        return <ApprovalInteractionUI interaction={interaction} payload={payload} onRespond={handleResponse} isResponding={isResponding()} />;
      case 'confirmation':
        return <ConfirmationInteractionUI interaction={interaction} payload={payload} onRespond={handleResponse} isResponding={isResponding()} />;
      case 'multiple-choice':
        return <MultipleChoiceInteractionUI interaction={interaction} payload={payload} onRespond={handleResponse} isResponding={isResponding()} />;
      case 'free-text':
        return <FreeTextInteractionUI interaction={interaction} payload={payload} onRespond={handleResponse} isResponding={isResponding()} />;
      case 'code-review':
        return <CodeReviewInteractionUI interaction={interaction} payload={payload} onRespond={handleResponse} isResponding={isResponding()} />;
      case 'discussion':
        return <DiscussionInteractionUI interaction={interaction} payload={payload} />;
      case 'file-selection':
        return <FileSelectionInteractionUI interaction={interaction} payload={payload} onRespond={handleResponse} isResponding={isResponding()} />;
      case 'priority-ranking':
        return <PriorityRankingInteractionUI interaction={interaction} payload={payload} onRespond={handleResponse} isResponding={isResponding()} />;
      case 'poll':
        return <PollInteractionUI interaction={interaction} payload={payload} />;
      case 'brainstorm':
        return <BrainstormInteractionUI interaction={interaction} payload={payload} />;
      case 'inspiration':
        return <InspirationInteractionUI interaction={interaction} payload={payload} onRespond={handleResponse} isResponding={isResponding()} />;
      default:
        return <GenericInteractionUI interaction={interaction} payload={payload} onRespond={handleResponse} isResponding={isResponding()} />;
    }
  };

  const handleResponse = async (payload: Record<string, unknown>) => {
    if (!selectedInteractionId()) return;

    setIsResponding(true);
    try {
      await respondToInteraction(selectedInteractionId()!, payload);
      refetchSelectedInteraction();
      refetch();
    } catch (error) {
      console.error('Failed to respond:', error);
      alert('Failed to submit response. Please try again.');
    } finally {
      setIsResponding(false);
    }
  };

  return (
    <div>
      {/* Cancel Confirmation Dialog */}
      <Show when={showCancelDialog()}>
        <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div class="bg-gray-800 rounded-lg p-6 max-w-md text-center border border-gray-700">
            <div class="mb-4 flex justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#f59e0b"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h2 class="text-xl font-medium mb-4">Cancel Interaction</h2>
            <p class="text-gray-400 mb-6">
              Are you sure you want to cancel this interaction? The agent will be notified.
            </p>
            <div class="flex justify-center gap-4">
              <button
                type="button"
                onClick={() => setShowCancelDialog(false)}
                disabled={isCancelling()}
                class="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors disabled:opacity-50"
              >
                Keep Open
              </button>
              <button
                type="button"
                onClick={handleCancelInteraction}
                disabled={isCancelling()}
                class="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-500 text-white transition-colors disabled:opacity-50"
              >
                {isCancelling() ? 'Cancelling...' : 'Cancel Interaction'}
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* Detail View */}
      <Show when={viewMode() === 'detail' && selectedInteraction()}>
        {(interaction) => (
          <div>
            {/* Detail Header */}
            <div class="mb-6">
              <div class="flex items-center gap-4 mb-4">
                <button
                  type="button"
                  onClick={handleBackToList}
                  class="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-200"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="m12 19-7-7 7-7" />
                    <path d="M19 12H5" />
                  </svg>
                </button>
                <div class="flex-1">
                  <h1 class="text-2xl font-semibold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                    {interaction().title}
                  </h1>
                  <p class="text-gray-400 text-sm mt-1">
                    {getInteractionTypeName(interaction().interactionTypeId)} - Created{' '}
                    {formatRelativeTime(interaction().createdAt)}
                  </p>
                </div>
                <Show when={interaction().status === 'pending' || interaction().status === 'in_progress'}>
                  <button
                    type="button"
                    onClick={() => setShowCancelDialog(true)}
                    class="px-4 py-2 rounded-lg bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </Show>
              </div>

              {/* Status badges */}
              <div class="flex flex-wrap gap-2">
                <span
                  class={`px-3 py-1 rounded-full text-sm border ${getStatusBadgeClass(
                    interaction().status,
                  )}`}
                >
                  {interaction().status === 'pending' && (
                    <span class="inline-block w-2 h-2 bg-yellow-400 rounded-full mr-2 animate-pulse" />
                  )}
                  {interaction().status.replace('_', ' ')}
                </span>
                <span
                  class={`px-3 py-1 rounded-full text-sm border ${getPriorityBadgeClass(
                    interaction().priority,
                  )}`}
                >
                  {interaction().priority}
                </span>
                <Show when={interaction().projectId}>
                  <A
                    href={`/projects/${interaction().projectId}`}
                    class="px-3 py-1 rounded-full text-sm bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 transition-colors"
                  >
                    {getProjectName(interaction().projectId)}
                  </A>
                </Show>
                <Show when={interaction().agentId}>
                  <span class="px-3 py-1 rounded-full text-sm bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                    {getAgentName(interaction().agentId)}
                  </span>
                </Show>
              </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content */}
              <div class="lg:col-span-2 space-y-6">
                {/* Description */}
                <Show when={interaction().description}>
                  <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                    <h2 class="text-lg font-medium text-gray-200 mb-4">Description</h2>
                    <p class="text-gray-300">{interaction().description}</p>
                  </section>
                </Show>

                {/* Response UI */}
                <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                  <h2 class="text-lg font-medium text-gray-200 mb-4">
                    {interaction().status === 'completed' ? 'Response' : 'Respond'}
                  </h2>
                  {renderInteractionResponseUI(interaction())}
                </section>

                {/* Context */}
                <Show when={interaction().requestContext}>
                  <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                    <h2 class="text-lg font-medium text-gray-200 mb-4">Context</h2>
                    <pre class="text-gray-300 text-sm whitespace-pre-wrap bg-gray-900/50 p-4 rounded-lg overflow-x-auto">
                      {JSON.stringify(interaction().requestContext, null, 2)}
                    </pre>
                  </section>
                </Show>
              </div>

              {/* Sidebar */}
              <div class="space-y-6">
                <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                  <h2 class="text-lg font-medium text-gray-200 mb-4">Details</h2>
                  <div class="space-y-4">
                    <div class="flex items-center justify-between">
                      <span class="text-gray-400">Status</span>
                      <span
                        class={`px-2 py-0.5 rounded-full text-xs border ${getStatusBadgeClass(
                          interaction().status,
                        )}`}
                      >
                        {interaction().status.replace('_', ' ')}
                      </span>
                    </div>
                    <div class="flex items-center justify-between">
                      <span class="text-gray-400">Priority</span>
                      <span
                        class={`px-2 py-0.5 rounded-full text-xs border ${getPriorityBadgeClass(
                          interaction().priority,
                        )}`}
                      >
                        {interaction().priority}
                      </span>
                    </div>
                    <div class="flex items-center justify-between">
                      <span class="text-gray-400">Type</span>
                      <span class="text-gray-200">
                        {getInteractionTypeName(interaction().interactionTypeId)}
                      </span>
                    </div>
                    <div class="flex items-center justify-between">
                      <span class="text-gray-400">Created</span>
                      <span class="text-gray-200 text-sm">
                        {new Date(interaction().createdAt).toLocaleString()}
                      </span>
                    </div>
                    <Show when={interaction().respondedAt}>
                      <div class="flex items-center justify-between">
                        <span class="text-gray-400">Responded</span>
                        <span class="text-gray-200 text-sm">
                          {new Date(interaction().respondedAt!).toLocaleString()}
                        </span>
                      </div>
                    </Show>
                    <Show when={interaction().timeoutAt}>
                      <div class="flex items-center justify-between">
                        <span class="text-gray-400">Timeout</span>
                        <span class="text-gray-200 text-sm">
                          {new Date(interaction().timeoutAt!).toLocaleString()}
                        </span>
                      </div>
                    </Show>
                  </div>
                </section>

                {/* Links */}
                <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                  <h2 class="text-lg font-medium text-gray-200 mb-4">Links</h2>
                  <div class="space-y-2">
                    <Show when={interaction().projectId}>
                      <A
                        href={`/projects/${interaction().projectId}`}
                        class="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-gray-900/50 hover:bg-gray-900 text-gray-300 hover:text-gray-100 transition-colors"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                          />
                        </svg>
                        View Project
                      </A>
                    </Show>
                    <Show when={interaction().executionId}>
                      <A
                        href={`/executions?executionId=${interaction().executionId}`}
                        class="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-gray-900/50 hover:bg-gray-900 text-gray-300 hover:text-gray-100 transition-colors"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                        View Execution
                      </A>
                    </Show>
                  </div>
                </section>
              </div>
            </div>
          </div>
        )}
      </Show>

      {/* List View */}
      <Show when={viewMode() === 'list'}>
        {/* Page Header */}
        <div class="mb-6">
          <div class="flex items-center justify-between">
            <div>
              <h1 class="text-2xl font-semibold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Interactions
              </h1>
              <p class="text-gray-400 text-sm mt-1">Human-in-the-loop requests from agents</p>
            </div>
            <div class="flex items-center gap-4">
              <Show when={pendingCount() > 0}>
                <div class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-yellow-500/20 text-yellow-400">
                  <span class="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                  <span class="text-sm font-medium">{pendingCount()} pending</span>
                </div>
              </Show>
              <label class="flex items-center gap-2 text-sm text-gray-400">
                <input
                  type="checkbox"
                  checked={autoRefresh()}
                  onChange={(e) => setAutoRefresh(e.currentTarget.checked)}
                  class="rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500"
                />
                Auto-refresh
              </label>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div class="mb-6 bg-gray-800/50 rounded-lg border border-gray-700 p-4">
          <div class="flex flex-wrap items-center gap-4">
            {/* Status Filter */}
            <select
              value={selectedStatus()}
              onChange={(e) => setSelectedStatus(e.currentTarget.value)}
              class="px-3 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="timed_out">Timed Out</option>
            </select>

            {/* Priority Filter */}
            <select
              value={selectedPriority()}
              onChange={(e) => setSelectedPriority(e.currentTarget.value)}
              class="px-3 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500"
            >
              <option value="">All Priority</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>

            {/* Type Filter */}
            <select
              value={selectedType()}
              onChange={(e) => setSelectedType(e.currentTarget.value)}
              class="px-3 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500"
            >
              <option value="">All Types</option>
              <For each={interactionTypes()}>
                {(type) => <option value={type.id}>{type.name}</option>}
              </For>
            </select>

            {/* Project Filter */}
            <select
              value={selectedProjectId()}
              onChange={(e) => setSelectedProjectId(e.currentTarget.value)}
              class="px-3 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-purple-500"
            >
              <option value="">All Projects</option>
              <For each={projects()}>
                {(project) => <option value={project.id}>{project.name}</option>}
              </For>
            </select>

            {/* Clear Filters */}
            <Show when={hasActiveFilters()}>
              <button
                type="button"
                onClick={clearFilters}
                class="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm transition-colors"
              >
                Clear Filters
              </button>
            </Show>

            {/* Refresh Button */}
            <button
              type="button"
              onClick={() => refetch()}
              class="ml-auto p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors"
              title="Refresh"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
                <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                <path d="M16 16h5v5" />
              </svg>
            </button>
          </div>
        </div>

        {/* Results Count and Page Size */}
        <div class="mb-4 flex items-center justify-between text-sm text-gray-400">
          <span>
            {totalCount()} interaction{totalCount() !== 1 ? 's' : ''} found
          </span>
          <div class="flex items-center gap-2">
            <span>Show:</span>
            <select
              value={pageSize()}
              onChange={(e) => setPageSize(Number(e.currentTarget.value) as PageSize)}
              class="px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-300 focus:outline-none focus:border-purple-500"
            >
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Loading State */}
        <Show when={interactionsData.loading}>
          <div class="flex items-center justify-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
          </div>
        </Show>

        {/* Empty State */}
        <Show when={!interactionsData.loading && interactions().length === 0}>
          <div class="text-center py-12">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="mx-auto text-gray-600 mb-4"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <h3 class="text-xl font-medium text-gray-300 mb-2">No interactions found</h3>
            <p class="text-gray-500 mb-6">
              {hasActiveFilters()
                ? 'Try adjusting your filters'
                : 'Agents will create interactions when they need human input'}
            </p>
          </div>
        </Show>

        {/* Interactions Table */}
        <Show when={!interactionsData.loading && interactions().length > 0}>
          <div class="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
            <table class="w-full">
              <thead class="bg-gray-900/50 border-b border-gray-700">
                <tr>
                  <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Status</th>
                  <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Title</th>
                  <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Type</th>
                  <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Priority</th>
                  <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Project</th>
                  <th class="px-4 py-3 text-left text-sm font-medium text-gray-400">Created</th>
                  <th class="px-4 py-3 text-right text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-700">
                <For each={interactions()}>
                  {(interaction) => (
                    <tr
                      class="hover:bg-gray-800/50 transition-colors cursor-pointer"
                      onClick={() => handleSelectInteraction(interaction.id)}
                    >
                      <td class="px-4 py-3">
                        <span
                          class={`px-2 py-0.5 rounded-full text-xs border ${getStatusBadgeClass(
                            interaction.status,
                          )}`}
                        >
                          {interaction.status === 'pending' && (
                            <span class="inline-block w-1.5 h-1.5 bg-yellow-400 rounded-full mr-1.5 animate-pulse" />
                          )}
                          {interaction.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td class="px-4 py-3">
                        <div class="flex flex-col gap-1">
                          <span class="text-sm text-gray-200 font-medium">{interaction.title}</span>
                          <Show when={interaction.description}>
                            <span class="text-xs text-gray-500 truncate max-w-xs">
                              {interaction.description}
                            </span>
                          </Show>
                        </div>
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-400">
                        {getInteractionTypeName(interaction.interactionTypeId)}
                      </td>
                      <td class="px-4 py-3">
                        <span
                          class={`px-2 py-0.5 rounded-full text-xs border ${getPriorityBadgeClass(
                            interaction.priority,
                          )}`}
                        >
                          {interaction.priority}
                        </span>
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-400">
                        {getProjectName(interaction.projectId)}
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-400">
                        {formatRelativeTime(interaction.createdAt)}
                      </td>
                      <td class="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectInteraction(interaction.id);
                          }}
                          class="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                        >
                          {interaction.status === 'pending' ? 'Respond' : 'View'}
                        </button>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>

        {/* Pagination */}
        <Show when={totalPages() > 1}>
          <div class="mt-6 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage() === 1}
              class="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>

            <For
              each={Array.from({ length: Math.min(5, totalPages()) }, (_, i) => {
                const page = currentPage();
                const total = totalPages();
                if (total <= 5) return i + 1;
                if (page <= 3) return i + 1;
                if (page >= total - 2) return total - 4 + i;
                return page - 2 + i;
              })}
            >
              {(page) => (
                <button
                  type="button"
                  onClick={() => setCurrentPage(page)}
                  class={`px-3 py-2 rounded-lg border transition-colors ${
                    currentPage() === page
                      ? 'bg-purple-600 border-purple-500 text-white'
                      : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600'
                  }`}
                >
                  {page}
                </button>
              )}
            </For>

            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages(), p + 1))}
              disabled={currentPage() === totalPages()}
              class="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </Show>
      </Show>
    </div>
  );
};

// ============================================================================
// Interaction Type UI Components
// ============================================================================

interface InteractionUIProps {
  interaction: Interaction;
  payload: Record<string, unknown>;
  onRespond?: (response: Record<string, unknown>) => void;
  isResponding?: boolean;
}

const ApprovalInteractionUI: Component<InteractionUIProps> = (props) => {
  const [comment, setComment] = createSignal('');

  const handleApprove = () => {
    props.onRespond?.({ approved: true, comment: comment() || undefined });
  };

  const handleReject = () => {
    props.onRespond?.({ approved: false, comment: comment() || undefined });
  };

  const isCompleted = () => props.interaction.status === 'completed';
  const response = () => props.interaction.responsePayload as { approved?: boolean; comment?: string } | undefined;

  return (
    <div class="space-y-4">
      <div class="p-4 bg-gray-900/50 rounded-lg">
        <p class="text-gray-200">{props.payload.message as string}</p>
        <Show when={props.payload.details}>
          <p class="text-gray-400 text-sm mt-2">{props.payload.details as string}</p>
        </Show>
      </div>

      <Show when={isCompleted()}>
        <div class={`p-4 rounded-lg border ${response()?.approved ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
          <div class="flex items-center gap-2">
            <span class={response()?.approved ? 'text-green-400' : 'text-red-400'}>
              {response()?.approved ? 'Approved' : 'Rejected'}
            </span>
          </div>
          <Show when={response()?.comment}>
            <p class="text-gray-400 text-sm mt-2">{response()?.comment}</p>
          </Show>
        </div>
      </Show>

      <Show when={!isCompleted()}>
        <textarea
          value={comment()}
          onInput={(e) => setComment(e.currentTarget.value)}
          placeholder="Add a comment (optional)"
          class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
          rows={3}
        />
        <div class="flex gap-3">
          <button
            type="button"
            onClick={handleApprove}
            disabled={props.isResponding}
            class="flex-1 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white font-medium transition-colors disabled:opacity-50"
          >
            {props.isResponding ? 'Submitting...' : 'Approve'}
          </button>
          <button
            type="button"
            onClick={handleReject}
            disabled={props.isResponding}
            class="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium transition-colors disabled:opacity-50"
          >
            {props.isResponding ? 'Submitting...' : 'Reject'}
          </button>
        </div>
      </Show>
    </div>
  );
};

const MultipleChoiceInteractionUI: Component<InteractionUIProps> = (props) => {
  const [selectedOptions, setSelectedOptions] = createSignal<string[]>([]);
  const options = () => (props.payload.options as string[]) || [];
  const allowMultiple = () => props.payload.allowMultiple as boolean;

  const toggleOption = (option: string) => {
    if (allowMultiple()) {
      setSelectedOptions((prev) =>
        prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
      );
    } else {
      setSelectedOptions([option]);
    }
  };

  const handleSubmit = () => {
    props.onRespond?.({
      selectedOptions: selectedOptions(),
      selectedOption: selectedOptions()[0],
    });
  };

  const isCompleted = () => props.interaction.status === 'completed';
  const response = () => props.interaction.responsePayload as { selectedOptions?: string[] } | undefined;

  return (
    <div class="space-y-4">
      <div class="p-4 bg-gray-900/50 rounded-lg">
        <p class="text-gray-200">{props.payload.question as string}</p>
      </div>

      <div class="space-y-2">
        <For each={options()}>
          {(option) => {
            const isSelected = () =>
              isCompleted()
                ? response()?.selectedOptions?.includes(option)
                : selectedOptions().includes(option);

            return (
              <button
                type="button"
                onClick={() => !isCompleted() && toggleOption(option)}
                disabled={isCompleted()}
                class={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                  isSelected()
                    ? 'bg-purple-500/20 border-purple-500/50 text-purple-300'
                    : 'bg-gray-900/50 border-gray-700 text-gray-300 hover:border-gray-600'
                } ${isCompleted() ? 'cursor-default' : 'cursor-pointer'}`}
              >
                <div class="flex items-center gap-3">
                  <div class={`w-5 h-5 rounded-${allowMultiple() ? 'md' : 'full'} border-2 flex items-center justify-center ${
                    isSelected() ? 'border-purple-500 bg-purple-500' : 'border-gray-600'
                  }`}>
                    <Show when={isSelected()}>
                      <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </Show>
                  </div>
                  {option}
                </div>
              </button>
            );
          }}
        </For>
      </div>

      <Show when={!isCompleted()}>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={props.isResponding || selectedOptions().length === 0}
          class="w-full px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-medium transition-colors disabled:opacity-50"
        >
          {props.isResponding ? 'Submitting...' : 'Submit'}
        </button>
      </Show>
    </div>
  );
};

const FreeTextInteractionUI: Component<InteractionUIProps> = (props) => {
  const [text, setText] = createSignal('');
  const maxLength = () => props.payload.maxLength as number | undefined;

  const handleSubmit = () => {
    props.onRespond?.({ text: text() });
  };

  const isCompleted = () => props.interaction.status === 'completed';
  const response = () => props.interaction.responsePayload as { text?: string } | undefined;

  return (
    <div class="space-y-4">
      <div class="p-4 bg-gray-900/50 rounded-lg">
        <p class="text-gray-200">{props.payload.prompt as string}</p>
      </div>

      <Show when={isCompleted()}>
        <div class="p-4 bg-gray-900/50 rounded-lg border border-gray-700">
          <p class="text-gray-200">{response()?.text}</p>
        </div>
      </Show>

      <Show when={!isCompleted()}>
        <div class="relative">
          <textarea
            value={text()}
            onInput={(e) => setText(e.currentTarget.value)}
            placeholder={props.payload.placeholder as string || 'Enter your response...'}
            maxLength={maxLength()}
            class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
            rows={5}
          />
          <Show when={maxLength()}>
            <span class="absolute bottom-2 right-2 text-xs text-gray-500">
              {text().length}/{maxLength()}
            </span>
          </Show>
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={props.isResponding || !text().trim()}
          class="w-full px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-medium transition-colors disabled:opacity-50"
        >
          {props.isResponding ? 'Submitting...' : 'Submit'}
        </button>
      </Show>
    </div>
  );
};

const CodeReviewInteractionUI: Component<InteractionUIProps> = (props) => {
  const [decision, setDecision] = createSignal<'approve' | 'request_changes' | 'comment' | null>(null);
  const [comments, setComments] = createSignal('');

  const handleSubmit = () => {
    props.onRespond?.({ decision: decision(), comments: comments() || undefined });
  };

  const isCompleted = () => props.interaction.status === 'completed';
  const response = () => props.interaction.responsePayload as { decision?: string; comments?: string } | undefined;

  return (
    <div class="space-y-4">
      <Show when={props.payload.description}>
        <div class="p-4 bg-gray-900/50 rounded-lg">
          <p class="text-gray-200">{props.payload.description as string}</p>
        </div>
      </Show>

      {/* Code Diff */}
      <Show when={props.payload.diff}>
        <div class="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
          <div class="px-4 py-2 bg-gray-800 border-b border-gray-700">
            <span class="text-sm text-gray-400">Code Changes</span>
          </div>
          <pre class="p-4 text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap">
            {props.payload.diff as string}
          </pre>
        </div>
      </Show>

      {/* Files */}
      <Show when={props.payload.files}>
        <div class="space-y-2">
          <For each={props.payload.files as Array<{ path: string; content: string; language?: string }>}>
            {(file) => (
              <div class="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
                <div class="px-4 py-2 bg-gray-800 border-b border-gray-700 flex items-center gap-2">
                  <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  <span class="text-sm text-gray-300">{file.path}</span>
                </div>
                <pre class="p-4 text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap max-h-96">
                  {file.content}
                </pre>
              </div>
            )}
          </For>
        </div>
      </Show>

      <Show when={isCompleted()}>
        <div class={`p-4 rounded-lg border ${
          response()?.decision === 'approve'
            ? 'bg-green-500/10 border-green-500/30'
            : response()?.decision === 'request_changes'
            ? 'bg-yellow-500/10 border-yellow-500/30'
            : 'bg-blue-500/10 border-blue-500/30'
        }`}>
          <span class={
            response()?.decision === 'approve'
              ? 'text-green-400'
              : response()?.decision === 'request_changes'
              ? 'text-yellow-400'
              : 'text-blue-400'
          }>
            {response()?.decision === 'approve' ? 'Approved' : response()?.decision === 'request_changes' ? 'Changes Requested' : 'Commented'}
          </span>
          <Show when={response()?.comments}>
            <p class="text-gray-400 text-sm mt-2">{response()?.comments}</p>
          </Show>
        </div>
      </Show>

      <Show when={!isCompleted()}>
        <textarea
          value={comments()}
          onInput={(e) => setComments(e.currentTarget.value)}
          placeholder="Add review comments..."
          class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
          rows={4}
        />
        <div class="flex gap-3">
          <button
            type="button"
            onClick={() => { setDecision('approve'); handleSubmit(); }}
            disabled={props.isResponding}
            class="flex-1 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white font-medium transition-colors disabled:opacity-50"
          >
            Approve
          </button>
          <button
            type="button"
            onClick={() => { setDecision('request_changes'); handleSubmit(); }}
            disabled={props.isResponding}
            class="flex-1 px-4 py-2 rounded-lg bg-yellow-600 hover:bg-yellow-500 text-white font-medium transition-colors disabled:opacity-50"
          >
            Request Changes
          </button>
          <button
            type="button"
            onClick={() => { setDecision('comment'); handleSubmit(); }}
            disabled={props.isResponding || !comments().trim()}
            class="flex-1 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors disabled:opacity-50"
          >
            Comment Only
          </button>
        </div>
      </Show>
    </div>
  );
};

const DiscussionInteractionUI: Component<InteractionUIProps> = (props) => {
  return (
    <div class="space-y-4">
      <div class="p-4 bg-gray-900/50 rounded-lg">
        <p class="text-gray-200">{props.payload.topic as string}</p>
        <Show when={props.payload.context}>
          <p class="text-gray-400 text-sm mt-2">{props.payload.context as string}</p>
        </Show>
      </div>

      <Show when={props.payload.questions}>
        <div class="space-y-2">
          <span class="text-sm text-gray-400">Questions to address:</span>
          <ul class="list-disc list-inside space-y-1">
            <For each={props.payload.questions as string[]}>
              {(question) => <li class="text-gray-300">{question}</li>}
            </For>
          </ul>
        </div>
      </Show>

      <div class="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <p class="text-blue-400 text-sm">
          This is a multi-response discussion. Use the responses section below to add messages.
        </p>
      </div>
    </div>
  );
};

const ConfirmationInteractionUI: Component<InteractionUIProps> = (props) => {
  const handleConfirm = () => {
    props.onRespond?.({ confirmed: true });
  };

  const handleCancel = () => {
    props.onRespond?.({ confirmed: false });
  };

  const isCompleted = () => props.interaction.status === 'completed';
  const response = () => props.interaction.responsePayload as { confirmed?: boolean } | undefined;

  return (
    <div class="space-y-4">
      <div class="p-4 bg-gray-900/50 rounded-lg">
        <p class="text-gray-200 font-medium">{props.payload.action as string}</p>
        <Show when={props.payload.description}>
          <p class="text-gray-400 text-sm mt-2">{props.payload.description as string}</p>
        </Show>
      </div>

      {/* Consequences */}
      <Show when={props.payload.consequences}>
        <div class="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span class="text-orange-400 font-medium">Consequences</span>
          </div>
          <ul class="list-disc list-inside space-y-1 text-gray-300 text-sm">
            <For each={props.payload.consequences as string[]}>
              {(consequence) => <li>{consequence}</li>}
            </For>
          </ul>
        </div>
      </Show>

      <Show when={isCompleted()}>
        <div class={`p-4 rounded-lg border ${response()?.confirmed ? 'bg-green-500/10 border-green-500/30' : 'bg-gray-500/10 border-gray-500/30'}`}>
          <span class={response()?.confirmed ? 'text-green-400' : 'text-gray-400'}>
            {response()?.confirmed ? 'Confirmed' : 'Cancelled'}
          </span>
        </div>
      </Show>

      <Show when={!isCompleted()}>
        <div class="flex gap-3">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={props.isResponding}
            class="flex-1 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-medium transition-colors disabled:opacity-50"
          >
            {props.isResponding ? 'Processing...' : 'Confirm'}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={props.isResponding}
            class="flex-1 px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-200 font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </Show>
    </div>
  );
};

const FileSelectionInteractionUI: Component<InteractionUIProps> = (props) => {
  const [selectedFiles, setSelectedFiles] = createSignal<string[]>([]);
  const files = () => (props.payload.files as Array<{ path: string; type?: string; size?: number }>) || [];
  const allowMultiple = () => props.payload.allowMultiple as boolean;

  const toggleFile = (path: string) => {
    if (allowMultiple()) {
      setSelectedFiles((prev) =>
        prev.includes(path) ? prev.filter((f) => f !== path) : [...prev, path]
      );
    } else {
      setSelectedFiles([path]);
    }
  };

  const handleSubmit = () => {
    props.onRespond?.({
      selectedFiles: selectedFiles(),
      selectedFile: selectedFiles()[0],
    });
  };

  const isCompleted = () => props.interaction.status === 'completed';
  const response = () => props.interaction.responsePayload as { selectedFiles?: string[] } | undefined;

  const formatFileSize = (size?: number) => {
    if (!size) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div class="space-y-4">
      <div class="p-4 bg-gray-900/50 rounded-lg">
        <p class="text-gray-200">{props.payload.prompt as string || 'Select files:'}</p>
      </div>

      <div class="space-y-2 max-h-96 overflow-y-auto">
        <For each={files()}>
          {(file) => {
            const isSelected = () =>
              isCompleted()
                ? response()?.selectedFiles?.includes(file.path)
                : selectedFiles().includes(file.path);

            return (
              <button
                type="button"
                onClick={() => !isCompleted() && toggleFile(file.path)}
                disabled={isCompleted()}
                class={`w-full text-left px-4 py-3 rounded-lg border transition-colors flex items-center gap-3 ${
                  isSelected()
                    ? 'bg-purple-500/20 border-purple-500/50 text-purple-300'
                    : 'bg-gray-900/50 border-gray-700 text-gray-300 hover:border-gray-600'
                } ${isCompleted() ? 'cursor-default' : 'cursor-pointer'}`}
              >
                <div class={`w-5 h-5 rounded-${allowMultiple() ? 'md' : 'full'} border-2 flex items-center justify-center shrink-0 ${
                  isSelected() ? 'border-purple-500 bg-purple-500' : 'border-gray-600'
                }`}>
                  <Show when={isSelected()}>
                    <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                    </svg>
                  </Show>
                </div>
                <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <div class="flex-1 min-w-0">
                  <div class="truncate">{file.path}</div>
                  <Show when={file.size}>
                    <span class="text-xs text-gray-500">{formatFileSize(file.size)}</span>
                  </Show>
                </div>
              </button>
            );
          }}
        </For>
      </div>

      <Show when={!isCompleted()}>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={props.isResponding || selectedFiles().length === 0}
          class="w-full px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-medium transition-colors disabled:opacity-50"
        >
          {props.isResponding ? 'Submitting...' : `Select ${selectedFiles().length} file${selectedFiles().length !== 1 ? 's' : ''}`}
        </button>
      </Show>
    </div>
  );
};

const PriorityRankingInteractionUI: Component<InteractionUIProps> = (props) => {
  const initialItems = () => (props.payload.items as string[]) || [];
  const [rankedItems, setRankedItems] = createSignal<string[]>(initialItems());

  const moveItem = (fromIndex: number, toIndex: number) => {
    const items = [...rankedItems()];
    const [item] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, item);
    setRankedItems(items);
  };

  const handleSubmit = () => {
    props.onRespond?.({ ranking: rankedItems() });
  };

  const isCompleted = () => props.interaction.status === 'completed';
  const response = () => props.interaction.responsePayload as { ranking?: string[] } | undefined;
  const displayItems = () => isCompleted() ? (response()?.ranking || []) : rankedItems();

  return (
    <div class="space-y-4">
      <div class="p-4 bg-gray-900/50 rounded-lg">
        <p class="text-gray-200">{props.payload.prompt as string || 'Rank these items by priority (drag to reorder):'}</p>
      </div>

      <div class="space-y-2">
        <For each={displayItems()}>
          {(item, index) => (
            <div class="flex items-center gap-3 px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg">
              <span class="w-6 h-6 rounded-full bg-purple-500/30 text-purple-300 text-sm flex items-center justify-center font-medium">
                {index() + 1}
              </span>
              <span class="flex-1 text-gray-200">{item}</span>
              <Show when={!isCompleted()}>
                <div class="flex gap-1">
                  <button
                    type="button"
                    onClick={() => index() > 0 && moveItem(index(), index() - 1)}
                    disabled={index() === 0}
                    class="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => index() < displayItems().length - 1 && moveItem(index(), index() + 1)}
                    disabled={index() === displayItems().length - 1}
                    class="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              </Show>
            </div>
          )}
        </For>
      </div>

      <Show when={!isCompleted()}>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={props.isResponding}
          class="w-full px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-medium transition-colors disabled:opacity-50"
        >
          {props.isResponding ? 'Submitting...' : 'Submit Ranking'}
        </button>
      </Show>
    </div>
  );
};

const PollInteractionUI: Component<InteractionUIProps> = (props) => {
  const options = () => (props.payload.options as string[]) || [];

  return (
    <div class="space-y-4">
      <div class="p-4 bg-gray-900/50 rounded-lg">
        <p class="text-gray-200">{props.payload.question as string}</p>
      </div>

      <div class="space-y-2">
        <For each={options()}>
          {(option) => (
            <div class="px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg">
              <div class="flex items-center justify-between">
                <span class="text-gray-200">{option}</span>
                <span class="text-gray-500 text-sm">0 votes</span>
              </div>
              <div class="mt-2 h-2 bg-gray-700 rounded-full overflow-hidden">
                <div class="h-full bg-purple-500 rounded-full" style={{ width: '0%' }} />
              </div>
            </div>
          )}
        </For>
      </div>

      <div class="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <p class="text-blue-400 text-sm">
          This is a multi-response poll. Multiple people can vote and results will be aggregated.
        </p>
      </div>
    </div>
  );
};

const BrainstormInteractionUI: Component<InteractionUIProps> = (props) => {
  return (
    <div class="space-y-4">
      <div class="p-4 bg-gray-900/50 rounded-lg">
        <p class="text-gray-200">{props.payload.topic as string}</p>
        <Show when={props.payload.context}>
          <p class="text-gray-400 text-sm mt-2">{props.payload.context as string}</p>
        </Show>
      </div>

      <Show when={props.payload.prompts}>
        <div class="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <svg class="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <span class="text-purple-400 font-medium">Brainstorm prompts:</span>
          </div>
          <ul class="list-disc list-inside space-y-1 text-gray-300 text-sm">
            <For each={props.payload.prompts as string[]}>
              {(prompt) => <li>{prompt}</li>}
            </For>
          </ul>
        </div>
      </Show>

      <div class="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <p class="text-blue-400 text-sm">
          This is a collaborative brainstorm. Add your ideas using the responses section below.
        </p>
      </div>
    </div>
  );
};

const InspirationInteractionUI: Component<InteractionUIProps> = (props) => {
  const [ideas, setIdeas] = createSignal('');
  const [references, setReferences] = createSignal('');

  const handleSubmit = () => {
    props.onRespond?.({
      ideas: ideas(),
      references: references() || undefined,
    });
  };

  const isCompleted = () => props.interaction.status === 'completed';
  const response = () => props.interaction.responsePayload as { ideas?: string; references?: string } | undefined;

  return (
    <div class="space-y-4">
      <div class="p-4 bg-gray-900/50 rounded-lg">
        <p class="text-gray-200">{props.payload.prompt as string || 'Share your creative ideas and inspiration:'}</p>
        <Show when={props.payload.context}>
          <p class="text-gray-400 text-sm mt-2">{props.payload.context as string}</p>
        </Show>
      </div>

      <Show when={props.payload.constraints}>
        <div class="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
          <div class="flex items-center gap-2 mb-2">
            <span class="text-orange-400 font-medium">Constraints to consider:</span>
          </div>
          <ul class="list-disc list-inside space-y-1 text-gray-300 text-sm">
            <For each={props.payload.constraints as string[]}>
              {(constraint) => <li>{constraint}</li>}
            </For>
          </ul>
        </div>
      </Show>

      <Show when={isCompleted()}>
        <div class="space-y-4">
          <div class="p-4 bg-gray-900/50 rounded-lg border border-gray-700">
            <span class="text-sm text-gray-400 block mb-2">Ideas shared:</span>
            <p class="text-gray-200">{response()?.ideas}</p>
          </div>
          <Show when={response()?.references}>
            <div class="p-4 bg-gray-900/50 rounded-lg border border-gray-700">
              <span class="text-sm text-gray-400 block mb-2">References:</span>
              <p class="text-gray-200">{response()?.references}</p>
            </div>
          </Show>
        </div>
      </Show>

      <Show when={!isCompleted()}>
        <div class="space-y-4">
          <div>
            <label class="block text-sm text-gray-400 mb-2">Your ideas and creative direction *</label>
            <textarea
              value={ideas()}
              onInput={(e) => setIdeas(e.currentTarget.value)}
              placeholder="Share your creative ideas, suggestions, and direction..."
              class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
              rows={5}
            />
          </div>
          <div>
            <label class="block text-sm text-gray-400 mb-2">References or examples (optional)</label>
            <textarea
              value={references()}
              onInput={(e) => setReferences(e.currentTarget.value)}
              placeholder="Links, examples, or references that might help..."
              class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
              rows={3}
            />
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={props.isResponding || !ideas().trim()}
            class="w-full px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-medium transition-colors disabled:opacity-50"
          >
            {props.isResponding ? 'Submitting...' : 'Submit Inspiration'}
          </button>
        </div>
      </Show>
    </div>
  );
};

const GenericInteractionUI: Component<InteractionUIProps> = (props) => {
  const [response, setResponse] = createSignal('');

  const handleSubmit = () => {
    try {
      const parsed = JSON.parse(response());
      props.onRespond?.(parsed);
    } catch {
      alert('Please enter valid JSON');
    }
  };

  const isCompleted = () => props.interaction.status === 'completed';

  return (
    <div class="space-y-4">
      <div class="bg-gray-900/50 rounded-lg border border-gray-700 overflow-hidden">
        <div class="px-4 py-2 bg-gray-800 border-b border-gray-700">
          <span class="text-sm text-gray-400">Request Payload</span>
        </div>
        <pre class="p-4 text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap">
          {JSON.stringify(props.payload, null, 2)}
        </pre>
      </div>

      <Show when={isCompleted()}>
        <div class="bg-gray-900/50 rounded-lg border border-gray-700 overflow-hidden">
          <div class="px-4 py-2 bg-gray-800 border-b border-gray-700">
            <span class="text-sm text-gray-400">Response</span>
          </div>
          <pre class="p-4 text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(props.interaction.responsePayload, null, 2)}
          </pre>
        </div>
      </Show>

      <Show when={!isCompleted()}>
        <textarea
          value={response()}
          onInput={(e) => setResponse(e.currentTarget.value)}
          placeholder='Enter JSON response...'
          class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none font-mono text-sm"
          rows={6}
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={props.isResponding || !response().trim()}
          class="w-full px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-medium transition-colors disabled:opacity-50"
        >
          {props.isResponding ? 'Submitting...' : 'Submit Response'}
        </button>
      </Show>
    </div>
  );
};

export default Interactions;
